import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
  timeout
} from 'rxjs/operators';

import { AuthFacade } from '@cpd-app/state/auth/auth.facade';

interface FeatureFlagDto {
  name?: string | null;
  isEnabled?: boolean | null;
}

export const GET_FEATURE_FLAGS = gql`
  query GetFeatureFlags {
    getFeatureFlags {
      name
      isEnabled
    }
  }
`;

/**
 * Reads the backend feature_flags table over GraphQL. These flags are UI hints
 * only — every flag-gated endpoint also enforces the flag server-side
 * (AP-002), so hiding UI here is convenience, not a security boundary.
 *
 * The query result is shared/replayed so multiple consumers in a session don't
 * each hit the network; on any error we fail closed (treat the flag as off) so
 * an unreachable backend never exposes a half-built feature.
 */
@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private flags$?: Observable<Map<string, boolean>>;

  constructor(
    private readonly apollo: Apollo,
    private readonly authFacade: AuthFacade
  ) {}

  private loadFlags(): Observable<Map<string, boolean>> {
    if (!this.flags$) {
      /*
       * WAIT FOR THE TOKEN. The comment below already knew this query goes out
       * before the token is in place and comes back denied. What it could not
       * see is what that denial then costs elsewhere: the interceptor reads a
       * 403 as an expired session and attempts a refresh, and this is the
       * FIRST denied call on a cold load, ahead of anything a page asks for.
       * Measured signed in, it still went out with no Authorization header.
       *
       * Two other callers were gated for the same reason. This one was the
       * remaining trigger, and it fires on every page rather than on one.
       */
      this.flags$ = this.authFacade.authToken$
        .pipe(
          filter((token) => !!token),
          take(1),
          // A visitor who never signs in still gets one attempt, and still
          // fails closed below, rather than the flags never resolving.
          timeout({ first: 3000, with: () => of('') }),
          switchMap(() =>
            this.apollo.query<{
              getFeatureFlags?: (FeatureFlagDto | null)[] | null;
            }>({
              query: GET_FEATURE_FLAGS
            })
          )
        )
        .pipe(
          map((res) => {
            const map = new Map<string, boolean>();
            for (const flag of res.data?.getFeatureFlags ?? []) {
              if (flag?.name) {
                map.set(flag.name, !!flag.isEnabled);
              }
            }
            return map;
          }),
          catchError(() => {
            // Fail closed for THIS read, but do not let one failure define the
            // session. getFeatureFlags is behind AuthGuard, so a request that
            // goes out before the token is in place (cold load, session
            // restore, token refresh) comes back 401 -- and the shareReplay
            // below would then serve that empty map to every consumer until the
            // page was reloaded. Every flag-gated feature would silently look
            // "turned off" while the flags were on in the database, which is
            // indistinguishable from a real misconfiguration and sends you
            // looking in the wrong place. Dropping the cached observable makes
            // the next reader retry instead.
            this.flags$ = undefined;
            return of(new Map<string, boolean>());
          }),
          shareReplay({ bufferSize: 1, refCount: false })
        );
    }
    return this.flags$;
  }

  /** Emits whether the named flag is enabled (false if absent or on error). */
  public isEnabled$(name: string): Observable<boolean> {
    return this.loadFlags().pipe(map((flags) => flags.get(name) ?? false));
  }
}
